import React, { useState } from "react";
import { Link } from "react-router-dom";
import { IoTimeOutline, IoWarningOutline } from "react-icons/io5";
import { useI18n } from "../contexts/I18n";
import { marketingI18n } from "../content/homeMarketing";
import { http } from "../lib/http";
import useScrollAnimate from "../hooks/useScrollAnimate";
import MarketingPageIntro from "./marketing/MarketingPageIntro";
import "./Home.css";
import "./MarketingPages.css";
import "./DeleteAccount.css";

export default function DeleteAccount() {
  const { lang } = useI18n();
  const pageT = marketingI18n[lang].pages.delete;
  useScrollAnimate([lang]);

  const [form, setForm] = useState({ email: "", name: "", reason: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const onChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.email?.trim()) {
      return setError(pageT.errorEmail);
    }

    if (confirmText.trim().toUpperCase() !== pageT.confirmWord.toUpperCase()) {
      return setError(pageT.errorConfirm);
    }

    setLoading(true);
    try {
      const res = await http.fetch("/api/delete-account/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email.trim(),
          name: form.name?.trim() || "",
          reason: form.reason?.trim() || "",
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || pageT.errorGeneric);
      }

      setSuccess(true);
      setForm({ email: "", name: "", reason: "" });
      setConfirmText("");
    } catch (err) {
      setError(err.message || pageT.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`marketing-page delete-page ${lang === "ar" ? "rtl" : ""}`}
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      <MarketingPageIntro variant="delete" page={pageT} lang={lang} layout="split" />

      <section className="delete-form-section" id="delete-form" aria-labelledby="delete-form-heading">
        <div className="container delete-form-layout">
          {success ? (
            <div className="delete-form delete-form--success scroll-animate from-bottom" role="alert">
              <span className="delete-success-icon" aria-hidden="true">
                ✓
              </span>
              <h2 id="delete-form-heading" className="delete-form__title">
                {pageT.successTitle}
              </h2>
              <p className="delete-form__lead">{pageT.successDesc}</p>
            </div>
          ) : (
            <form className="delete-form scroll-animate from-bottom" onSubmit={handleSubmit} noValidate>
              <h2 id="delete-form-heading" className="delete-form__title">
                {pageT.formTitle}
              </h2>
              <p className="delete-form__lead">{pageT.formLead}</p>

              {error && (
                <p className="delete-form__error" role="alert">
                  {error}
                </p>
              )}

              <label className="delete-field">
                <span>{pageT.emailLabel}</span>
                <input
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  placeholder={pageT.emailPh}
                  value={form.email}
                  onChange={onChange("email")}
                />
              </label>

              <label className="delete-field">
                <span>{pageT.nameLabel}</span>
                <input
                  type="text"
                  name="name"
                  autoComplete="name"
                  placeholder={pageT.namePh}
                  value={form.name}
                  onChange={onChange("name")}
                />
              </label>

              <label className="delete-field">
                <span>{pageT.reasonLabel}</span>
                <textarea
                  name="reason"
                  rows={4}
                  placeholder={pageT.reasonPh}
                  value={form.reason}
                  onChange={onChange("reason")}
                />
              </label>

              <label className="delete-field">
                <span>{pageT.confirmLabel}</span>
                <input
                  type="text"
                  name="confirm"
                  autoComplete="off"
                  placeholder={pageT.confirmWord}
                  value={confirmText}
                  onChange={(e) => {
                    setConfirmText(e.target.value);
                    if (error) setError("");
                  }}
                />
              </label>

              <button type="submit" className="delete-form__submit" disabled={loading} aria-busy={loading}>
                {loading ? pageT.submitting : pageT.submit}
              </button>
            </form>
          )}

          <aside className="delete-aside scroll-animate from-bottom delay-2">
            <div className="delete-aside__card">
              <span className="delete-aside__icon" aria-hidden="true">
                <IoTimeOutline />
              </span>
              <h3 className="delete-aside__title">{pageT.nextTitle}</h3>
              <ol className="delete-aside__steps">
                {pageT.nextSteps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </div>
            <div className="delete-aside__card">
              <span className="delete-aside__icon" aria-hidden="true">
                <IoWarningOutline />
              </span>
              <h3 className="delete-aside__title">{pageT.noteTitle}</h3>
              <p className="delete-aside__desc">{pageT.noteDesc}</p>
            </div>
          </aside>
        </div>
      </section>

      <section className="delete-cta-section">
        <div className="container delete-cta-inner scroll-animate from-bottom delay-2">
          <h2 className="delete-cta-title">{pageT.ctaTitle}</h2>
          <p className="delete-cta-desc">{pageT.ctaDesc}</p>
          <div className="delete-cta-actions">
            <Link to={pageT.ctaSecondaryTo} className="delete-cta-btn delete-cta-btn--primary">
              {pageT.ctaSecondary}
            </Link>
            <Link to="/contact" className="delete-cta-btn delete-cta-btn--secondary">
              {pageT.ctaContact}
            </Link>
          </div>
        </div>
      </section>

      <div className="section-wave section-wave--light" aria-hidden="true" />
    </div>
  );
}
